import React, { useEffect, useState, useRef } from 'react';
import { useFixedAttributeDisplayNames } from '../../hooks/useFixedAttributeDisplayNames';
import { css, StyleSheet } from 'aphrodite';
import { useSelector } from 'react-redux';
import { Dropdown, Pagination, Table } from 'antd';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import ReconnectingWebSocket from 'reconnecting-websocket';

import {
  convertPxToRem,
  filterQuoteStatus,
  filterQuoteStatusColor,
  getValueFromObjectByKeys,
  handleStatus,
  translate
} from '../../helper';
import TextBase from '../../common/text';
import { Colors } from '../../themes/Colors';
import { sizeOfBaseButton } from '../../utils/defined';
import { SVGImageComponentReact } from '../../themes/Images';
import APIManager from '../../connection/ApiManager';
import Search from '../../common/search';
import Link from '../../common/link';
import { ROUTER_NAME } from '../../router/routers';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Utils from '../../utils';
import { getSessionFilter, setSessionFilter } from '../../utils/CommonFunction';
import { ENV } from '../../Env';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import {
  Button,
  IconButton,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  styled
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CustomSpinner from '../../common/CustomSpinner';
import PageTitleHeader from '../../common/page-title-header';

const heightOfBottomBlackAlpha = `calc(var(--fs-hero)*6.69)`;

const DashboardBox = styled('div')(({ theme }) => ({
  backgroundColor: '#ffffff',
  backgroundImage: 'linear-gradient(135deg, #f5f7fa 0%, #f9fafb 100%)',
  overflowY: 'auto',
  padding: 'calc(var(--sp-lg)*1.5) calc(var(--sp-lg)*1.6) calc(var(--sp-md2)*1)',
  '& .ant-table-container': {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  '& .ant-table': {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(229, 231, 235, 0.6)',
    borderCollapse: 'collapse',
    '&:hover': {
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04)'
    }
  },
  '& .ant-table-thead > tr': {
    backgroundColor: 'linear-gradient(135deg, #1A5A78 0%, #0F3D52 100%)',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important'
  },
  '& .ant-table-thead > tr > th': {
    backgroundColor: '#1A5A78 !important',
    color: '#ffffff !important',
    borderBottom: '2px solid #0F3D52 !important',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important',
    transition: 'all 0.3s ease',
    letterSpacing: '0.035em',
    boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.3), inset 0 -1px 0 rgba(0, 0, 0, 0.1)',
    whiteSpace: 'nowrap'
  },
  '& .ant-table-tbody > tr': {
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    borderBottom: '1px solid rgba(229, 231, 235, 0.7) !important',
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor:
        'linear-gradient(90deg, rgba(26, 90, 120, 0.06) 0%, rgba(26, 90, 120, 0.02) 100%) !important',
      boxShadow: `
      0 12px 28px rgba(15, 23, 42, 0.10),
      0 2px 8px rgba(15, 23, 42, 0.06)
    `,
      zIndex: 2,

      background:
        'linear-gradient(90deg, rgba(26,90,120,0.04) 0%, rgba(255,255,255,1) 50%, rgba(26,90,120,0.04) 100%)',
      transform: 'translateY(-2px) scale(1.004)'
    },
    '&:last-child': {
      borderBottom: 'none !important'
    }
  },
  '& .ant-table tbody tr': {
    border: 'none !important'
  },
  '& .ant-table-tbody > tr > td': {
    color: '#1f2937',
    transition: 'all 0.2s ease',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8) !important',
    borderRight: 'none !important',
    borderLeft: 'none !important',
    borderTop: 'none !important',
    backgroundColor: '#ffffff'
  },
  '& .ant-table-tbody > tr:last-child > td': {
    borderBottom: 'none !important'
  },
  '& .ant-table-tbody > tr:hover > td': {
    backgroundColor: 'rgba(26, 90, 120, 0.03) !important',
    borderTop: '1px solid rgba(26,90,120,0.08)',
    borderBottom: '1px solid rgba(26,90,120,0.08)'
  },
  '& .MuiOutlinedInput-input': {
    padding: 'var(--sp-md2) calc(var(--sp-md2)*1.4)'
  },
  '& .MuiButton-root': {
    backgroundColor: '#ffffff !important',
    color: '#374151 !important',
    border: '1.5px solid #e5e7eb !important',
    borderRadius: 'var(--sp-md2)',
    fontWeight: '500',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
    fontSize: 'calc(var(--fs-xs) * 1.5)',
    '&:hover': {
      backgroundColor: '#f9fafb !important',
      borderColor: '#d1d5db !important',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  },
  '& .MuiButtonGroup-root': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04) !important',
    height: 'calc(var(--fs-xl)*2.35)',
    borderRadius: 'var(--sp-md2)',
    border: '1.5px solid #e5e7eb !important',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease'
  }
}));

const ActionButton = styled(Button)(({}) => ({
  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
  color: '#1f2937 !important',
  textTransform: 'capitalize',
  fontWeight: '600 !important',
  padding: 'calc(var(--sp-sm)*1.1) calc(var(--sp-md2)*1.5)',
  borderRadius: 'var(--sp-md2)',
  border: '1.5px solid #d1d5db !important',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontSize: 'calc(var(--fs-xs) * 1.55)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease'
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
    transform: 'translateY(-3px)',
    borderColor: '#9ca3af !important',
    '&::before': {
      left: '100%'
    }
  },
  '&:active': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)'
  },
  '& .icon': {
    fontSize: 'calc(var(--fs-sm2) * 1.1)',
    color: '#6b7280',
    marginLeft: 'calc(var(--sp-md2)*1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: '700'
  },
  '&:hover .icon': {
    transform: 'translateX(3px)',
    color: '#1f2937'
  }
}));

const styles = StyleSheet.create({
  container: {
    textAlign: 'left',
    whiteSpace: 'pre-line',
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 'calc(var(--sp-lg)*1.5) calc(var(--sp-lg)*1.6) calc(var(--sp-md2)*1)'
  },
  dashboardTitle: {
    marginBottom: `calc(var(--sp-lg)*2.6)`,
    marginTop: `calc(var(--sp-lg)*2)`
  },
  bottomBlackAlpha: {
    width: '100%',
    minWidth: '100vw',
    height: heightOfBottomBlackAlpha,
    backgroundColor: Colors.black80,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    flexDirection: 'column',
    zIndex: 1,
    position: 'absolute'
  },
  bgOverlay: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    background:
      'linear-gradient(90deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.56) 100%)',
    zIndex: 1,
    overflow: 'hidden'
  },
  wrapText: {
    position: 'relative',
    zIndex: 4,
    maxWidth: '50%',
    height: '100%',
    minHeight: `calc(var(--fs-hero)*10)`,
    paddingLeft: `calc(var(--sp-lg)*5)`
  },
  buttonAction: {
    width: sizeOfBaseButton.width,
    height: sizeOfBaseButton.height,
    marginBottom: `calc(var(--sp-lg)*1.55)`
  }
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box
          sx={{ py: 'calc(var(--sp-lg)*1.132)' }}
          style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '0px' }}>
          <Typography
            style={{
              width: '100%',
              padding: '0px',
              backgroundColor: 'transparent',
              borderRadius: '10px',
              boxShadow: 'none'
            }}>
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

const quoteStatusColor = (status) => {
  switch (status) {
    case 'SENT':
      return Colors.primary;
    case 'ACCEPTED':
      return Colors.open_order;
    case 'NS':
      return Colors.red;
    case 'INPROGRESS':
      return Colors.mainColor;
    default:
      return Colors.red;
  }
};

let socket = null;

function Dashboard() {
  const navigate = useNavigate();
  const whiteLabel = useSelector((state) => state.User?.branding);
  const { token, user, profile } = useSelector((state) => state.User?.userInfo);

  const isUserServiceWriter = user?.role === 'SW';
  const isUserTeamMember = profile?.team_member;

  const PaymentFilterOptions = [
    { label: 'All Order', value: 'all_order' },
    { label: 'Stripe', value: 'stripe' },
    { label: translate('accountBilling'), value: 'account' }
  ];

  const [value, setValue] = useState(0);
  const [childAccounts, setChildAccounts] = useState([]);
  const initialChildAccountFilters = getSessionFilter('childAccounts', null) || {
    page: 1,
    limit: 15,
    sortBy: '-created_at',
    textSearch: ''
  };
  const [childAccountPage, setChildAccountPage] = useState(initialChildAccountFilters.page);
  const [childAccountPageSize, setChildAccountPageSize] = useState(
    initialChildAccountFilters.limit
  );
  const [sortBy, setSortBy] = useState(initialChildAccountFilters.sortBy);
  const [textSearch, setTextSearch] = useState(initialChildAccountFilters.textSearch);
  const [totalChildAccounts, setTotalChildAccounts] = useState(0);
  const [loading, setLoading] = useState(true);

  const [orders, setOrders] = useState([]);
  const [orderPage, setOrdertPage] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(15);
  const [orderSortBy, setOrderSortBy] = useState('-created_at');
  const [orderSearch, setOrderSearch] = useState('');
  const [totalParentOrders, setTotalParentOrders] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('all_order');

  const [visibleDropdownSort, setVisibleDropdownSort] = useState(false);
  const sortByAnchorRef = useRef(null);
  const [openPaymentFilter, setOpenPaymentFilter] = useState(false);
  const [paymentAnchorEl, setPaymentAnchorEl] = useState(null);
  const { fixedAttributeDisplayNames } = useFixedAttributeDisplayNames();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    try {
      setSessionFilter('childAccounts', {
        page: 1,
        limit: childAccountPageSize,
        sortBy: sortOption,
        textSearch
      });
    } catch (e) {}
  };

  const childAccountColumns = [
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('location')}</span>
      ),
      dataIndex: 'location',
      align: 'left',
      key: 'location',
      render: (order, row) => {
        return (
          <span style={{ fontSize: 'calc(var(--fs-xs) * 1.6)' }}>
            {row?.vendors?.[0]?.business_name || ''}
          </span>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('dateCreated')}</span>
      ),
      dataIndex: 'created_at',
      align: 'left',
      key: 'created_at',
      render: (value) => {
        return (
          <span style={{ fontSize: 'calc(var(--fs-xs) * 1.6)' }}>
            {value ? moment(value, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY hh:mm a') : ''}
          </span>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('customerPo')}</span>
      ),
      dataIndex: 'id',
      align: 'left',
      key: 'id',
      render: (text, row) => {
        return (
          <Typography
            color={'rgb(81, 125, 152)'}
            fontSize={'calc(var(--fs-xs) * 1.6)'}
            sx={{
              cursor: 'pointer',
              textDecorationLine: 'underline'
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${ROUTER_NAME.orderDetail}/${row.id}`);
            }}>
            {row.id}
          </Typography>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>
          {fixedAttributeDisplayNames?.['order_name'] || translate('orderName')}
        </span>
      ),
      dataIndex: 'name',
      align: 'left',
      key: 'name',
      render: (order, row) => {
        return <span style={{ fontSize: 'calc(var(--fs-xs) * 1.6)' }}>{order || ''}</span>;
      }
    },
    {
      title: <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('status')}</span>,
      dataIndex: 'id',
      align: 'left',
      key: 'status',
      render: (value, record) => {
        const { quotes, status } = record;
        const singleQuote = quotes && quotes[0];
        const quoteStatus = singleQuote?.status;

        const deliverLabelMap = {
          instore: translate('acceptedInStorePickup'),
          delivery: translate('acceptedDelivery')
        };

        const statusLabel = handleStatus(record, isUserServiceWriter, isUserTeamMember);
        const statusColor = filterQuoteStatusColor(
          statusLabel,
          isUserServiceWriter,
          isUserTeamMember
        );
        const statusName = filterQuoteStatus(statusLabel, isUserServiceWriter, isUserTeamMember);
        return (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '6px',
                fontSize: 'calc(var(--fs-xs) * 1.6)',
                fontWeight: 500,
                letterSpacing: '0.01em',
                color: statusColor,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
                cursor: 'default'
              }}>
              {statusName}
            </Box>
            {record?.status === 'INPROGRESS' && !quoteStatus && (
              <Typography
                fontSize={'calc(var(--fs-xs) * 1.6)'}
                color={'#000'}>{` - ${record?.order_vendor_note_user_name?.trim()}`}</Typography>
            )}
            {statusName === 'Paid' && (
              <Typography
                fontSize={'calc(var(--fs-xs) * 1.6)'}
                variant="p"
                fontWeight={'400'}
                color={'#000'}>
                {` - ${deliverLabelMap[record?.delivery_method]}`}
              </Typography>
            )}
            {statusName === 'Service Accepted' && (
              <Typography
                fontSize={'calc(var(--fs-xs) * 1.6)'}
                display={'block'}
                variant="p"
                fontWeight={'400'}
                color={'#000'}>
                {deliverLabelMap[record?.delivery_method]}
              </Typography>
            )}
          </>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>
          {translate('warrantyStatus')}
        </span>
      ),
      key: 'tags',
      dataIndex: 'tags',
      align: 'left',
      render: (value, item) => {
        return (
          <>
            {item.warranty ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CheckBoxIcon
                  sx={{
                    background: 'white',
                    borderRadius: 'calc(var(--fs-xl) * 2.35)',
                    padding: 'var(--sp-xs)',
                    fontSize: 'calc(var(--fs-xl) * 1.415)',
                    color: 'green'
                  }}
                />{' '}
                <span style={{ marginLeft: 'var(--sp-xxs)', fontSize: 'calc(var(--fs-xs) * 1.6)' }}>
                  {item.warranty_user_name}
                </span>
              </div>
            ) : (
              <CheckBoxOutlineBlankIcon
                sx={{
                  background: 'white',
                  borderRadius: 'calc(var(--fs-xl) * 2.35)',
                  padding: 'var(--sp-xs)',
                  fontSize: 'calc(var(--fs-xl) * 1.415)'
                }}
              />
            )}
          </>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('fulfilledBy')}</span>
      ),
      dataIndex: 'email',
      align: 'left',
      key: 'email',
      render: (text, record) => {
        const quote = record.quotes?.filter((q) => q.status === 'ACCEPTED');
        const pending = record.quotes?.find((q) => q.status === 'SENT');
        return (
          <span style={{ fontSize: 'calc(var(--fs-xs) * 1.6)' }}>
            {quote.length > 0 ? quote?.[0]?.user?.name : pending?.id ? pending?.user?.name : 'N/A'}
          </span>
        );
      }
    },
    {
      title: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span
            style={{
              fontSize: 'calc(var(--fs-sm2) * 1.015)',
              color: paymentMethod !== 'all_order' ? '#FBBF24' : '#ffffff',
              fontWeight: paymentMethod !== 'all_order' ? 700 : 600,
              transition: 'all 0.2s ease'
            }}>
            {translate('paymentInfo')}
          </span>
          <IconButton
            size="small"
            title="Filter by payment method"
            aria-label="Filter by payment method"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              const virtualAnchor = {
                getBoundingClientRect: () => ({
                  width: 0,
                  height: 0,
                  x: r.left + r.width / 2,
                  y: r.bottom + 4,
                  top: r.bottom + 4,
                  left: r.left + r.width / 2,
                  right: r.left + r.width / 2,
                  bottom: r.bottom + 4
                })
              };
              setPaymentAnchorEl(virtualAnchor);
              setOpenPaymentFilter((p) => !p);
            }}
            sx={{
              p: 'calc(var(--sp-xxs)*.471698)',
              position: 'relative',
              borderRadius: '999px',
              backgroundColor:
                paymentMethod !== 'all_order' ? 'rgba(251, 191, 36, 0.18)' : 'transparent',

              '&:hover': {
                backgroundColor:
                  paymentMethod !== 'all_order'
                    ? 'rgba(251, 191, 36, 0.25)'
                    : 'rgba(255,255,255,0.12)'
              }
            }}>
            <FilterListIcon
              sx={{
                fontSize: 'calc(var(--fs-xs) * 1.698113)',
                color: paymentMethod !== 'all_order' ? '#FBBF24' : '#ffffff',
                transition: 'all 0.2s ease'
              }}
            />
            {paymentMethod !== 'all_order' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#FBBF24',
                  boxShadow: '0 0 0 2px #0B5C7A'
                }}
              />
            )}
          </IconButton>
        </Box>
      ),
      key: 'tags',
      dataIndex: 'tags',
      align: 'left',
      render: (value, item) => {
        if (!item?.quotes?.[0]) {
          return <span style={{ fontSize: 'calc(var(--fs-xs) * 1.6)' }}></span>;
        }

        const isStripe = item?.quotes?.[0]?.stripe_charge_id !== 'NS';
        const label = isStripe ? translate('stripe') : translate('accountBilling');
        const Icon = isStripe ? CreditCardIcon : ReceiptLongIcon;

        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              letterSpacing: 'calc(var(--sp-xxs)*0.0943396))',
              color: isStripe ? '#1E40AF' : '#6B21A8',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(2px)',
              cursor: 'default'
            }}>
            <Typography
              component="span"
              sx={{
                fontSize: 'calc(var(--fs-xs) * 1.6)',
                color: 'inherit',
                whiteSpace: 'nowrap',
                letterSpacing: 'calc(var(--sp-xxs)*0.0943396))'
              }}>
              {label}
            </Typography>
          </Box>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('orderInfo')}</span>
      ),
      dataIndex: 'orderInfo',
      align: 'left',
      key: 'orderInfo',
      render: (text, item) => {
        return (
          <ActionButton
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${ROUTER_NAME.orderDetail}/${item?.id}`);
            }}
            style={{ margin: 0 }}>
            <span style={{ fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>{translate('details')}</span>
            <ArrowForwardIosIcon className="icon" />
          </ActionButton>
        );
      }
    }
  ];

  const getChildAcountData = async () => {
    let params = {
      sort_by: sortBy,
      ...(textSearch.length > 0 ? { search: textSearch } : {}),
      page: childAccountPage,
      page_size: childAccountPageSize
    };
    if (paymentMethod === 'stripe') params.payment_method = '1';
    if (paymentMethod === 'account') params.payment_method = '2';
    let { data } = await APIManager.getInstance().getChildAccounts(params);
    setChildAccounts(data?.data || []);
    if (data?.page?.total_records !== totalChildAccounts) {
      setTotalChildAccounts(data?.page?.total_records || 0);
    }
    try {
      setSessionFilter('childAccounts', {
        page: childAccountPage || 1,
        limit: childAccountPageSize,
        sortBy,
        textSearch
      });
    } catch (e) {}
    setLoading(false);
  };

  const getParentOrderData = async () => {
    let { data } = await APIManager.getInstance().getParentOrders({
      sort_by: sortBy,
      ...(orderSearch.length > 0 ? { search: orderSearch } : {}),
      page: orderPage,
      page_size: orderPageSize
    });
    setOrders(data?.data || []);
    if (data?.page?.total_records !== totalParentOrders) {
      setTotalParentOrders(data?.page?.total_records || 0);
    }
    setLoading(false);
  };

  const handleSearch = (text) => {
    if (value === 0) setTextSearch(text);
    if (value === 1) setOrderSearch(text);
  };

  const handlePaymentFilterSelect = (val) => {
    setPaymentMethod(val);
    setOpenPaymentFilter(false);
    setPaymentAnchorEl(null);
  };
  const fetchData = () => {
    if (value === 0) getChildAcountData();
    if (value === 1) getParentOrderData();
  };

  const getUpdates = () => {
    socket = new ReconnectingWebSocket(
      `wss://${Utils.constants.API_URL.replace('https://', '')}ws/orders/?token=${token}`
    );
    socket.onmessage = (event) => {
      if (window.location.pathname.includes('dashboard')) {
        fetchData();
      }
    };
  };

  const onPageChange = (pageNumber) => {
    const p = pageNumber || 1;
    setChildAccountPage(p);
    try {
      setSessionFilter('childAccounts', {
        page: p,
        limit: childAccountPageSize,
        sortBy,
        textSearch
      });
    } catch (e) {}
  };

  const onOrderPageChange = (pageNumber) => {
    setOrdertPage(pageNumber);
  };

  const handlePageSizeChange = (cur, size) => {
    setChildAccountPageSize(size);
    setChildAccountPage(1);
    try {
      setSessionFilter('childAccounts', {
        page: 1,
        limit: size,
        sortBy,
        textSearch
      });
    } catch (e) {}
  };

  useEffect(() => {
    let title = ENV.CURRENT_HOST_NAME.toUpperCase();
    if (whiteLabel?.location_name) {
      title = whiteLabel?.location_name;
    }
    document.title = title;
  }, [whiteLabel]);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData();
    getUpdates();
  }, [
    textSearch,
    orderSearch,
    sortBy,
    orderSortBy,
    childAccountPage,
    orderPage,
    value,
    childAccountPageSize,
    orderPageSize,
    paymentMethod
  ]);

  const placeholder = value === 0 ? 'searchChildAccount' : 'searchOrders';

  return (
    <DashboardBox>
      <PageTitleHeader
        title={translate('allLocationOrders')}
      />
      <div
        style={{
          padding: 'var(--sp-lg) 0'
        }}>
        <div className="flex-row-center-between width-100">
          <div>
            <Search
              placeholder={translate(placeholder)}
              tooltip="Search by Location name, PO#, order name or fulfilled by."
              value={value === 0 ? textSearch : orderSearch}
              setQuery={(text) => {
                handleSearch(text);
              }}
              sx={{
                width: 'calc(var(--fs-hero)*8.254)',
                '& .MuiOutlinedInput-input': {
                  fontSize: 'var(--fs-md2) !important',
                  padding: 'var(--sp-md2) var(--sp-md2*1.4)'
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  fontSize: 'var(--fs-md2)',
                  padding: 'var(--sp-md2) var(--sp-md2*1.4)',
                  lineHeight: '100%'
                }
              }}
            />
          </div>
          <div className="flex-row-center">
            <Button
              ref={sortByAnchorRef}
              onClick={() => setVisibleDropdownSort(!visibleDropdownSort)}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 700,
                height: 'calc(var(--fs-xl)*2.35) !important',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderRadius: 'var(--sp-md2)',
                fontSize: 'calc(var(--fs-sm2) * 1.015)',
                backgroundColor: '#ffffff !important',
                border: '1.5px solid #e5e7eb !important',
                color: '#374151 !important',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                padding: 'calc(var(--sp-md)*1) calc(var(--sp-md2)*1.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 'calc(var(--sp-xxs)*1.5)',
                '&:hover': {
                  backgroundColor: '#f3f4f6 !important',
                  borderColor: '#d1d5db !important',
                  boxShadow:
                    '0 12px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)'
                }
              }}>
              <span style={{ fontWeight: 500, color: '#374151' }}>{translate('sortBy')}</span>
              <span style={{ fontWeight: 600, color: '#1A5A78' }}>
                {sortBy === '-created_at'
                  ? translate('newestToOldest')
                  : translate('oldestToNewest')}
              </span>
              <ArrowDropDownIcon
                sx={{
                  fontSize: 'calc(var(--fs-xl) * 1.132075)',
                  marginLeft: 'auto',
                  transition: 'transform 0.2s ease',
                  transform: visibleDropdownSort ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#9ca3af'
                }}
              />
            </Button>
            <Popper
              sx={{
                zIndex: 9999,
                textTransform: 'capitalize'
              }}
              open={visibleDropdownSort}
              anchorEl={sortByAnchorRef.current}
              placement="bottom-start"
              strategy="fixed"
              role={undefined}
              transition
              disablePortal
              modifiers={[
                {
                  name: 'sameWidth',
                  enabled: true,
                  phase: 'beforeWrite',
                  requires: ['computeStyles'],
                  fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                  }
                },
                {
                  name: 'preventOverflow',
                  options: {
                    altBoundary: true,
                    tether: false,
                    padding: 8
                  }
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4]
                  }
                }
              ]}>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                  }}>
                  <Paper sx={{ width: '100%' }}>
                    <ClickAwayListener onClickAway={() => setVisibleDropdownSort(false)}>
                      <MenuList autoFocusItem>
                        <MenuItem
                          selected={sortBy === '-created_at'}
                          onClick={() => {
                            handleSortChange('-created_at');
                            setVisibleDropdownSort(false);
                          }}
                          style={{
                            color: sortBy === '-created_at' ? '#1A5A78' : '#000',
                            fontSize: 'var(--fs-md2)',
                            fontWeight: sortBy === '-created_at' ? '600' : '500'
                          }}>
                          {translate('newestToOldest')}
                        </MenuItem>
                        <MenuItem
                          selected={sortBy === 'created_at'}
                          onClick={() => {
                            handleSortChange('created_at');
                            setVisibleDropdownSort(false);
                          }}
                          style={{
                            color: sortBy === 'created_at' ? '#1A5A78' : '#000',
                            fontSize: 'var(--fs-md2)',
                            fontWeight: sortBy === 'created_at' ? '600' : '500'
                          }}>
                          {translate('oldestToNewest')}
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        </div>
      </div>
      <CustomTabPanel value={value} index={0}>
        <Table
          style={{ width: '100%' }}
          columns={childAccountColumns}
          dataSource={childAccounts}
          className="table"
          bordered={false}
          loading={{ spinning: loading, indicator: <CustomSpinner fullPage /> }}
          total={totalChildAccounts}
          pagination={false}
          components={{
            header: {
              cell: ({ children, ...rest }) => {
                return (
                  <th
                    {...rest}
                    style={{
                      padding: 'calc(var(--sp-md2)*1.7) !important',
                      cursor: 'default',
                      letterSpacing: '0.025em',
                      whiteSpace: 'nowrap'
                    }}>
                    {children}
                  </th>
                );
              }
            }
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if (!e.currentTarget.closest('th')) {
                navigate(`${ROUTER_NAME.orderDetail}/${record.id}`);
              }
            },
            style: {
              cursor: 'pointer',
              transition: 'all 0.25s ease'
            }
          })}
        />
        {!loading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 'calc(var(--sp-md2)*1.9)'
            }}>
            <TextBase
              style={{
                color: '#000'
              }}
              size={'var(--fs-md2)'}>
              {translate('showing')}
              <b>&nbsp;{(childAccountPage - 1) * childAccountPageSize + 1}</b> {translate('to')}
              <b>
                &nbsp;{Math.min(childAccountPage * childAccountPageSize, totalChildAccounts)}
              </b>{' '}
              {translate('of')}
              <b>&nbsp;{totalChildAccounts}</b> {translate('results')}
            </TextBase>
            <Pagination
              className="custom-pagination"
              current={childAccountPage}
              pageSize={childAccountPageSize}
              total={totalChildAccounts}
              onChange={onPageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </CustomTabPanel>
      <Popper
        sx={{ zIndex: 99999 }}
        open={openPaymentFilter}
        anchorEl={paymentAnchorEl}
        placement="bottom"
        strategy="fixed"
        transition
        disablePortal
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } }
        ]}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
            }}>
            <Paper
              sx={{
                minWidth: 'calc(var(--fs-hero)*4.24528)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
              <ClickAwayListener
                onClickAway={() => {
                  setOpenPaymentFilter(false);
                  setPaymentAnchorEl(null);
                }}>
                <MenuList autoFocusItem>
                  {PaymentFilterOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      selected={option.value === paymentMethod}
                      onClick={() => {
                        handlePaymentFilterSelect(option.value);
                      }}
                      sx={{
                        fontSize: 'var(--fs-md2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                      <span style={{ flex: 1 }}>{option.label}</span>
                      {option.value === paymentMethod && (
                        <CheckIcon sx={{ fontSize: 'var(--fs-xl)' }} />
                      )}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </DashboardBox>
  );
}

const mapStateToProps = (state) => {
  return {
    userDetail: getValueFromObjectByKeys(state, ['User', 'userDetail']),
    userInfo: getValueFromObjectByKeys(state, ['User', 'userInfo']),
    listNotification: getValueFromObjectByKeys(state, ['User', 'notification'])
  };
};
const mapDispatchToProps = (dispatch) => {
  return {};
};
export default Dashboard;
